import React, { useEffect, useState } from 'react';
import { DownloadIcon, Share2Icon } from 'lucide-react';
import { canShareFiles, downloadBlob, profileCardFileName, renderProfileCard, shareProfileCard } from '../../utils/shareCard';
import type { ArchetypeResult } from '../../types/assessment';
import { Button } from '../Button';
type ProfileCardActionsProps = {
  profile: ArchetypeResult[];
};
export function ProfileCardActions({
  profile
}: ProfileCardActionsProps) {
  const [busy, setBusy] = useState<'download' | 'share' | null>(null);
  const [error, setError] = useState('');
  const [shareSupported, setShareSupported] = useState(false);
  useEffect(() => {
    setShareSupported(canShareFiles());
  }, []);
  const fileName = profileCardFileName(profile);
  const handleDownload = async () => {
    setBusy('download');
    setError('');
    try {
      const blob = await renderProfileCard(profile);
      downloadBlob(blob, fileName);
    } catch {
      setError('The image could not be created. Try the print option below instead.');
    } finally {
      setBusy(null);
    }
  };
  const handleShare = async () => {
    setBusy('share');
    setError('');
    try {
      const blob = await renderProfileCard(profile);
      await shareProfileCard(blob, fileName, profile[0].name);
    } catch (shareError) {
      // A user cancelling the OS share sheet is not an error worth surfacing.
      if ((shareError as Error)?.name !== 'AbortError') {
        setError('Sharing is not available here. You can download the image instead.');
      }
    } finally {
      setBusy(null);
    }
  };
  return <div className="no-print" data-id="element-56" data-mp="10vhfv70kfp97">
      <div className="flex flex-col gap-2.5 sm:flex-row" data-id="element-57" data-mp="10witnj8yupum">
        <Button onClick={handleDownload} disabled={busy !== null} data-id="element-58" data-mp="vwwgf9t519lt">
          <DownloadIcon size={17} aria-hidden="true" data-id="element-59" data-mp="vyh7uqk5ocfg" />
          {busy === 'download' ? 'Preparing your card…' : 'Download My Archetype Profile'}
        </Button>

        {shareSupported && <Button variant="outline" onClick={handleShare} disabled={busy !== null} data-id="element-60" data-mp="lz8jcd827q0h">
            <Share2Icon size={17} aria-hidden="true" data-id="element-61" data-mp="c99ps5vwes1s" />
            Share My Results
          </Button>}
      </div>

      <p className="mt-2.5 text-xs text-ink-muted" data-id="element-62" data-mp="h7usqd2jbptx">
        A high-resolution PNG of your full profile. Your email address is never included.
      </p>

      {error && <p role="alert" className="mt-2 text-xs text-[#b94040]" data-id="element-63" data-mp="7cjdzur4mim6">
          {error}
        </p>}
    </div>;
}